import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';

import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { FinanceService } from 'app/finance/finance.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { MessageService } from 'app/services/messages.service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { BankStatement } from '../bank-statement.model';

interface BankAccountOption {
  accountId: number;
  accountName: string;
  accountNumber: string;
  currencyValue?: string;
}

@Component({
  selector: 'app-move-bank-statement-dialog',
  templateUrl: './move-bank-statement-dialog.component.html',
  styleUrls: ['./move-bank-statement-dialog.component.css']
})
export class MoveBankStatementDialogComponent implements OnInit {
  bankStatement: BankStatement;
  sourceEntityId: number;
  currentAccountNumber: string;
  currentAccountId: number;
  clientSearchPlaceholder = MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER;

  clientSearchForm: FormGroup;
  selectedClient: GenericSearchDetails = null;
  clientSearchResults: GenericSearchDetails[] = [];
  searchingClients = false;

  clientAccounts: BankAccountOption[] = [];
  selectedAccount: BankAccountOption = null;
  loadingAccounts = false;
  moving = false;

  constructor(
    public dialogRef: MdDialogRef<MoveBankStatementDialogComponent>,
    private formBuilder: FormBuilder,
    private requestService: ObservableRequestService,
    private financeService: FinanceService,
    private snackBarService: SnackBarService
  ) {
    this.clientSearchForm = this.formBuilder.group({
      clientSearch: ['', Validators.required]
    });
  }

  ngOnInit() {
    if (this.bankStatement && this.bankStatement.bankAccount) {
      this.currentAccountNumber = this.bankStatement.bankAccount.accountNumber;
      this.currentAccountId = this.bankStatement.bankAccount.accountId;
    }
  }

  searchClients(searchTerm: string) {
    if (!searchTerm || searchTerm.trim().length < 2) {
      this.clientSearchResults = [];
      this.selectedClient = null;
      this.clientAccounts = [];
      this.selectedAccount = null;
      return;
    }

    this.searchingClients = true;
    const searchQuery = encodeURIComponent(searchTerm.trim());
    this.requestService.getRequest(`${ConstantsService.CLIENT_SEARCH}/${searchQuery}`).subscribe(
      (response: any) => {
        this.searchingClients = false;
        this.clientSearchResults = this.parseClientSearchResponse(response);
      },
      (error: any) => {
        this.searchingClients = false;
        this.snackBarService.showSimpleMsj('Error searching clients: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private parseClientSearchResponse(response: any): GenericSearchDetails[] {
    const results: GenericSearchDetails[] = [];
    if (!response || !response.arrResult) {
      return results;
    }

    const organisations = response.arrResult.organisations || [];
    for (const org of organisations) {
      const clientDetails = new GenericSearchDetails();
      clientDetails.id = org.id;
      clientDetails.clientId = org.clientId;
      clientDetails.entityId = org.entityId;
      clientDetails.clientName = org.name;
      clientDetails.clientType = 'Organisation';
      clientDetails.entityType = 'Organisation';
      results.push(clientDetails);
    }

    const persons = response.arrResult.persons || [];
    for (const person of persons) {
      const clientDetails = new GenericSearchDetails();
      clientDetails.id = person.id;
      clientDetails.clientId = person.clientId;
      clientDetails.entityId = person.entityId;
      clientDetails.clientName = this.buildPersonName(person);
      clientDetails.clientType = 'Person';
      clientDetails.entityType = 'Person';
      results.push(clientDetails);
    }

    return results;
  }

  private buildPersonName(person: any): string {
    const parts = [person.firstNames, person.middleNames, person.lastNames].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(' ');
    }
    return person.name || '';
  }

  onClientSearchChange(value: string) {
    this.searchClients(value);
  }

  onClientSelected(client: GenericSearchDetails) {
    this.selectedClient = client;
    this.selectedAccount = null;
    this.loadClientAccounts();
  }

  loadClientAccounts() {
    if (!this.selectedClient || !this.selectedClient.entityId) {
      return;
    }

    this.loadingAccounts = true;
    this.clientAccounts = [];
    this.financeService.getBankInstitutionsAccountsForEntity(this.selectedClient.entityId).subscribe(
      (bankInstitutions: any) => {
        this.loadingAccounts = false;
        const accountList = this.flattenBankAccounts(bankInstitutions);
        const isSameClient = this.sourceEntityId != null
          && this.selectedClient.entityId === this.sourceEntityId;

        this.clientAccounts = accountList.filter(acc => {
          if (!isSameClient || !this.currentAccountId) {
            return true;
          }
          return acc.accountId !== this.currentAccountId;
        });
      },
      (error: any) => {
        this.loadingAccounts = false;
        this.snackBarService.showSimpleMsj('Error loading accounts: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private flattenBankAccounts(bankInstitutions: any): BankAccountOption[] {
    const institutions = Array.isArray(bankInstitutions)
      ? bankInstitutions
      : (bankInstitutions && bankInstitutions.arrResult ? bankInstitutions.arrResult : []);

    const accounts: BankAccountOption[] = [];
    institutions.forEach((institution: any) => {
      if (institution.bankAccounts && Array.isArray(institution.bankAccounts)) {
        institution.bankAccounts.forEach((account: any) => {
          if (account && account.accountId) {
            accounts.push(account);
          }
        });
      }
    });
    return accounts;
  }

  isSharedAccount(account: BankAccountOption): boolean {
    return this.currentAccountId != null && account.accountId === this.currentAccountId;
  }

  onAccountSelected(account: BankAccountOption) {
    this.selectedAccount = account;
  }

  canMove(): boolean {
    return !this.moving
      && this.selectedClient != null
      && this.selectedClient.clientId != null
      && this.selectedAccount != null
      && this.selectedAccount.accountId != null;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMove() {
    if (!this.canMove() || !this.bankStatement || !this.bankStatement.id) {
      return;
    }

    this.moving = true;
    this.financeService.moveBankStatement(
      this.bankStatement.id,
      this.selectedClient.clientId,
      this.selectedAccount.accountId
    ).subscribe(
      (response: any) => {
        this.moving = false;
        const result = response.arrResult || response;
        this.dialogRef.close({ success: true, result: result });
      },
      (error: any) => {
        this.moving = false;
        const message = (error && error.message) ? error.message : 'Unknown error';
        this.snackBarService.showSimpleMsj('Error moving bank statement: ' + message);
      }
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/finance/move-bank-statement-dialog/move-bank-statement-dialog.component.ts